<template>
  <section class="section">
    <div class="container">
      <Title>Packages</Title>

      <div class="margin-bottom-1">
        <CheckboxText
          css="is-inline-block margin-right-10"
          :checked="internalExternal"
          @click="
            tempFilter = '';
            internalExternal = true;
            internalExternal === true ? getLatestPackages() : false;
          "
          >Packages in this group</CheckboxText
        >

        <CheckboxText
          css="is-inline-block"
          :checked="!internalExternal"
          @click="
            tempFilter = '';
            internalExternal = false;
            search('');
          "
          >All available packages</CheckboxText
        >

        <CheckboxText
          v-if="!internalExternal"
          css
          :checked="includePrerelease"
          @click="
            includePrerelease = !includePrerelease;
            focus(searchId);
          "
          >Include pre-release</CheckboxText
        >
      </div>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="true"
        @submit="search($event)"
      ></SearchBar>

      <Tips
        v-show="firstLoadDone && visiblePackagesLength !== 0"
        path="groupPackages"
        xKey="dismissedTips"
        property="autoUpdateLatestVersionStats"
      >
        Tip: Change the package version to see the installation status for that
        version.
      </Tips>

      <LoaderOne v-show="!firstLoadDone"></LoaderOne>

      <p v-show="!searching && visiblePackagesLength === 0">
        <i>No packages.</i>
      </p>

      <transition name="fade" appear>
        <table
          v-show="!searching && visiblePackagesLength !== 0"
          class="table is-hoverable is-narrow is-fullwidth"
        >
          <thead>
            <tr class="is-unselectable">
              <th class="is-hidden-touch">Icon</th>
              <th @click="dispatchGroupOrder('chocoPackage.Title')">Title</th>
              <th @click="dispatchGroupOrder('source.name')">Repo</th>

              <th @click="dispatchGroupOrder('chocoPackage.IsLatestVersion')">
                Version
              </th>
              <th
                @click="dispatchGroupOrder('chocoPackage.PackageScanStatus')"
                class="is-hidden-touch"
              >
                Health
              </th>
              <th @click="dispatchGroupOrder('command.name')">Action</th>
              <th @click="dispatchGroupOrder('command.name')">
                Command
              </th>
              <th
                class="is-hidden-touch"
                @click="dispatchGroupOrder('command.added', true)"
              >
                Added
              </th>

              <th @click="dispatchGroupOrder('stats.procentWorking', true)">
                Stats
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="arr of filtered($store.state.groupPackage.mergedPackages)"
            >
              <tr
                v-for="item in selected(arr)"
                :key="key(item)"
                :id="item.chocoPackage.Id"
              >
                <td class="is-hidden-touch">
                  <figure class="image is-24x24">
                    <img :src="icon(item)" alt />
                  </figure>
                </td>
                <td>
                  <a
                    class="has-text-link"
                    :data-tooltip="item.chocoPackage.Title"
                    @click="toggle(item, 'showInfo', true)"
                    >{{ title(item) }}</a
                  >
                </td>

                <td>
                  <router-link :to="`/repos/${item.source.uid}/`">
                    <span v-if="!isPseudo(item)">{{ item.source.name }}</span>
                    <span
                      v-else
                      data-tooltip="Package was not found in repo"
                      class="has-text-danger"
                    >
                      {{ item.source.name }}
                    </span>
                  </router-link>
                </td>

                <td>
                  <Versions
                    :items="arr"
                    :isGroup="true"
                    @change="jump($event)"
                  ></Versions>
                </td>
                <td class="is-hidden-touch">
                  <NotApproved :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </NotApproved>

                  <FailedValidations :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </FailedValidations>

                  <FailedTests :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </FailedTests>

                  <FailedVirusScan :item="item" :showTooltip="true">
                    <i class="mdi mdi-alert-outline has-text-warning"></i>
                  </FailedVirusScan>

                  <Passed :item="item" :showTooltip="false">
                    <i class="mdi mdi-check has-text-success"></i>
                  </Passed>
                </td>
                <td>
                  <InstallButton
                    :item="item"
                    @click="toggle(item, 'showActions', true)"
                  ></InstallButton>

                  <UninstallButton
                    :item="item"
                    @click="toggle(item, 'showActions', true)"
                  ></UninstallButton>
                </td>
                <td>
                  <div>
                    <div
                      v-if="item.command && item.command.status === 'pending'"
                      class="tags has-addons"
                    >
                      <span class="tag is-rounded is-light is-capitalized">
                        {{ item.command.name }}
                      </span>

                      <span
                        v-if="hasParam(item, '-f')"
                        class="tag is-rounded is-light"
                        data-tooltip="Forced"
                        >-f</span
                      >

                      <span
                        v-if="hasParam(item, '-m')"
                        class="tag is-rounded is-light"
                        data-tooltip="Side by side installation"
                        >-m</span
                      >

                      <span
                        v-if="hasParam(item, '-n')"
                        class="tag is-rounded is-light"
                        data-tooltip="Add/remove package only"
                        >-n</span
                      >

                      <span
                        v-if="hasValidSchedule(item)"
                        class="tag is-rounded is-primary is-light"
                        data-tooltip="Scheduled"
                      >
                        <i class="mdi mdi-calendar-month"></i>
                      </span>

                      <span
                        v-if="hasAutoUpgrade(arr)"
                        class="tag is-rounded is-primary is-light"
                        data-tooltip="Auto upgrade"
                      >
                        <i class="mdi mdi-cached"></i>
                      </span>
                    </div>
                  </div>
                </td>

                <td class="is-hidden-touch">
                  {{ fromNow(item.command.added) }}
                </td>
                <td style="min-width: 200px" @click="showGroupStats(item, 1)">
                  <ChartBar
                    v-if="item.stats"
                    :data="chartData(item)"
                    class="clickable"
                  ></ChartBar>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </transition>

      <Paging
        v-if="visiblePackagesLength > 0 && !searching"
        :items="paging.pages"
        :selected="paging.page"
        @change="setPage($event)"
        :size="paging.max"
        @size="setPageMax($event)"
        :hasOptions="true"
      ></Paging>
    </div>

    <Info
      v-if="selectedItem && showInfo"
      v-model="showInfo"
      :item="selectedItem"
      @openActions="showActions = true"
    ></Info>

    <GroupActions
      v-if="showActions && selectedItem"
      v-model="showActions"
      :item="selectedItem"
    ></GroupActions>

    <GroupStats
      v-if="selectedItem && showStats"
      v-model="showStats"
      :item="selectedItem"
      :tab="statsTab"
      @changed="statsTab = $event"
    ></GroupStats>

    <ActionInfo
      v-if="selectedItem && showActionInfo"
      v-model="showActionInfo"
      :item="selectedItem"
      @hide="selectedItem = null"
      @openActions="toggle(null, 'showActions', true)"
    ></ActionInfo>
  </section>
</template>

<style lang="css" scoped>
th {
  font-weight: normal;
}
</style>

<script>
import moment from "moment";
import { hasParam, toIsoDate, getPackageNameVersionAndHash } from "@/utils/mdc";
import { isScheduleValid, objectifySchedule } from "@/utils/packageDate";
import { focus, getId, jump } from "@/utils/document";
import packageStatuses from "@/utils/packageStatus";
import { filterPackages } from "@/utils/packageFilter";
import { dispatchGroupOrder } from "@/utils/packageOrder";
import odata from "@/utils/packageOdata";

import {
  getStartIndex,
  getPackageNumbers,
  getPackageObj,
} from "@/utils/paging";

import GroupActions from "@/components/main/packages/GroupActions.vue";
import Info from "@/components/main/packages/Info.vue";
export default {
  components: {
    Tips: () => import("@/components/main/Tips.vue"),
    Paging: () => import("@/components/main/paging/Nav.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    Versions: () => import("@/components/main/packages/Versions.vue"),
    ActionInfo: () => import("@/components/main/packages/ActionInfo.vue"),
    GroupStats: () => import("@/components/main/packages/GroupStats.vue"),
    CheckboxText: () => import("@/components/singles/CheckboxText.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    LoaderOne: () => import("@/components/singles/LoaderOne.vue"),
    NotApproved: () =>
      import("@/components/main/packages/warnings/NotApproved.vue"),
    FailedValidations: () =>
      import("@/components/main/packages/warnings/FailedValidations.vue"),
    FailedTests: () =>
      import("@/components/main/packages/warnings/FailedTests.vue"),
    FailedVirusScan: () =>
      import("@/components/main/packages/warnings/FailedVirusScan.vue"),
    Passed: () => import("@/components/main/packages/warnings/Passed.vue"),
    InstallButton: () =>
      import("@/components/main/packages/buttons/Install.vue"),
    UninstallButton: () =>
      import("@/components/main/packages/buttons/Uninstall.vue"),
    ChartBar: () => import("@/components/main/group/ChartBar.vue"),

    GroupActions,
    Info,
  },
  watch: {
    $route(to, from) {
      if (from.params.groupUID !== to.params.groupUID) this.init(true);
    },
  },
  computed: {
    selectedItem() {
      return this.$store.getters["groupPackage/selectedItem"];
    },
    chartData() {
      return (item) => {
        return [
          {
            percent: item.stats.procentWorking,
            css: "has-background-info",
            text: "Working",
          },
          {
            percent: item.stats.procentInstalled,
            css: "has-background-success",
            text: "Installed",
          },
          {
            percent: item.stats.procentUninstalled,
            css: "has-background-danger",
            text: "Uninstalled",
          },
          {
            percent: item.stats.procentFailed,
            css: "has-background-warning",
            text: "Failed",
          },
          {
            percent: item.stats.procentNoStatus,
            css: "has-background-grey-light",
            text: "No status",
          },
        ];
      };
    },
    key() {
      return (item) => {
        return (
          item.chocoPackage.Id + item.chocoPackage.Version + item.source.uid
        );
      };
    },
    selected() {
      return (arr) => {
        return arr.filter((x) => x.selected);
      };
    },
    filtered() {
      return (packagesObj) => {
        if (this.filter && this.filter !== "") {
          const newPackagesObj = filterPackages(packagesObj, this.filter);
          this.visiblePackagesLength = Object.keys(newPackagesObj).length;

          return getPackageObj(
            newPackagesObj,
            this.paging.start,
            this.paging.page * this.paging.max
          );
        } else {
          this.visiblePackagesLength = Object.keys(packagesObj).length;
          return getPackageObj(
            packagesObj,
            this.paging.start,
            this.paging.page * this.paging.max
          );
        }
      };
    },
    hasValidSchedule() {
      return (item) => {
        return isScheduleValid(objectifySchedule(item.schedule));
      };
    },
    hasAutoUpgrade() {
      return (items) => {
        return items.some(
          (x) =>
            isScheduleValid(objectifySchedule(x.schedule)) &&
            x.schedule.type === 1
        );
      };
    },
    name() {
      return (item) => {
        return this.$helpers.getNameOrFriendlyName(item);
      };
    },
    isPseudo() {
      return (item) => {
        return item?.chocoPackage?.Pseudo;
      };
    },
    icon() {
      return (item) => {
        return item.chocoPackage.IconUrl && item.chocoPackage.IconUrl !== ""
          ? item.chocoPackage.IconUrl
          : "/box.png";
      };
    },
    hasParam() {
      return (item, param) => {
        return hasParam(item.command.parameters, param);
      };
    },
    toIso() {
      return (date) => {
        return toIsoDate(date);
      };
    },
    nameAndVersion() {
      return (item) => {
        return getPackageNameVersionAndHash(item);
      };
    },
    title() {
      return (item) => {
        return item.chocoPackage.Title.length > 30
          ? `${item.chocoPackage.Title.substring(0, 30)}...`
          : item.chocoPackage.Title;
      };
    },

    uninstallable() {
      return (item) => {
        return packageStatuses.isUninstallable(item);
      };
    },
    fromNow() {
      return (date) => {
        if (date) {
          return moment(date).fromNow();
        } else {
          return "";
        }
      };
    },
  },
  data() {
    return {
      visiblePackagesLength: 0,
      firstLoadDone: false,
      searching: false,
      internalExternal: true,
      showActions: false,
      showStats: false,
      showInfo: false,
      showActionInfo: false,
      tempFilter: "",
      filter: "",
      includePrerelease: false,
      statsTab: 1,
      searchId: getId(),
      paging: {
        page: 1,
        max: 15,
        start: 0,
        pages: [],
      },
    };
  },
  beforeDestroy() {
    this.$store.commit("groupPackage/clear");
  },
  async created() {
    this.init();
  },
  methods: {
    toggle(item, what, showHide) {
      if (item && item.command) {
        this.$store.commit("groupPackage/selectItem", item);
      }

      this[what] = showHide;
    },
    jump(item) {
      jump(item.chocoPackage.Id);
    },
    async init(force) {
      if (force) {
        this.internalExternal = true;
        this.filter = "";
        this.tempFilter = this.filter;
        this.$store.commit("groupPackage/clear");
        this.showActions = false;
      }

      try {
        await this.$store.dispatch(
          "settings/getUserSettings",
          { path: "groupPackages" },
          { root: true }
        );

        const paging = this.$store.state.settings.user.groupPackages?.paging;
        if (paging) this.paging.max = paging.max;

        await this.getLatestPackages();
      } catch (err) {
        console.log(err);
        this.$helpers.notify("Error", "Could not get data.", "danger", 5000);
      }

      this.firstLoadDone = true;
    },
    setPageMax(size) {
      this.paging.max = size;

      this.$store.dispatch("settings/setUser", {
        path: "groupPackages",
        key: "paging",
        value: { max: size },
      });

      this.setPage(1);
      this.setPages(
        Object.keys(this.$store.state.groupPackage.mergedPackages).length
      );
    },
    setPages(num) {
      this.paging.pages = getPackageNumbers(num, this.paging.max);
    },
    setPage(page) {
      this.paging.start = getStartIndex(page, this.paging.max);
      this.paging.page = page;
    },
    dispatchGroupOrder: dispatchGroupOrder,
    focus: focus,
    async getLatestPackages() {
      this.searching = true;

      await this.$store.dispatch("groupPackage/fetchAndMerge", {
        domainUID: this.$route.params.domainUID,
        groupUID: this.$route.params.groupUID,
      });

      this.setPage(1);

      this.setPages(
        Object.keys(this.$store.state.groupPackage.mergedPackages).length
      );

      this.searching = false;
      focus(this.searchId);
    },
    async search(searchStr) {
      this.searching = true;

      if (!this.internalExternal) {
        this.filter = "";

        let encodedFilter = "";
        if (searchStr !== "") {
          encodedFilter = odata.getSearchString(
            searchStr,
            this.includePrerelease,
            null,
            this.paging.max
          );
        } else {
          encodedFilter = odata.getLatestPackagesString(
            this.paging.max,
            this.includePrerelease
          );
        }

        const filters = this.$store.state.source.sources.map((x) => {
          return { source: x, filter: encodedFilter };
        });

        await this.$store.dispatch("groupPackage/searchFetchAndMerge", {
          domainUID: this.$route.params.domainUID,
          groupUID: this.$route.params.groupUID,
          filters: filters,
        });

        this.setPage(1);
        this.setPages(
          Object.keys(this.$store.state.groupPackage.mergedPackages).length
        );
      } else {
        this.setPage(1);
        this.filter = searchStr;
      }

      this.searching = false;
      focus(this.searchId);
    },
    showGroupStats(item, selectedTab) {
      this.statsTab = selectedTab;
      this.toggle(item, "showStats", true);
    },
  },
};
</script>
